// Company details (FR-40); a company_admin can edit them.
import { Alert, Button, Loader, Paper, Stack, Table, Text, Title } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { CompanyStatusBadge, companyKey, MINE_KEY, type Company } from "./common";
import { CompanyForm, type CompanyDetails } from "./CompanyForm";

export function OverviewTab({ company: summary, admin }: { company: Company; admin: boolean }) {
  const { t } = useTranslation();
  const f = useFormat();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const details = useQuery({
    queryKey: companyKey(summary.id, "details"),
    queryFn: () => unwrap(api.GET("/companies/{company_id}", { params: { path: { company_id: summary.id } } })),
  });

  const save = async (body: CompanyDetails): Promise<string | null> => {
    const { data, error } = await api.PATCH("/companies/{company_id}", {
      params: { path: { company_id: summary.id } },
      body,
    });
    if (!data) return errorMessage(error, t("error.generic"));
    queryClient.setQueryData(companyKey(summary.id, "details"), data);
    void queryClient.invalidateQueries({ queryKey: MINE_KEY });
    setEditing(false);
    setSaved(true);
    return null;
  };

  if (details.isPending) return <Loader size="sm" aria-label={t("common.loading")} />;
  if (details.isError) return <FormError message={errorMessage(details.error, t("error.generic"))} />;
  const c = details.data;
  const rows: [string, React.ReactNode][] = [
    [t("company.public_ref"), c.public_ref],
    [t("company.text_name"), c.text_name || "—"],
    [t("business.form_segment"), t(`segment.${c.segment}`, { defaultValue: c.segment })],
    [t("business.form_registration"), c.registration_number || "—"],
    [t("business.form_contact_name"), c.contact_name],
    [t("business.form_contact_email"), c.contact_email],
    [t("business.form_contact_phone"), c.contact_phone || "—"],
    [t("company.status"), <CompanyStatusBadge key="s" status={c.status} />],
    [t("company.plan"), c.plan],
    [t("company.created"), f.date(c.created_at)],
  ];

  return (
    <Stack gap="md">
      {saved && <Notice>{t("company.saved")}</Notice>}
      {c.status === "pending" && (
        <Alert color="yellow" title={t("company.pending_title")} data-testid="company-pending">
          {t("company.pending_body")}
        </Alert>
      )}
      {c.status === "suspended" && (
        <Alert color="red" title={t("company.suspended_title")}>
          {t("company.suspended_body")}
        </Alert>
      )}
      {c.text_name && c.text_name !== c.name && (
        <Alert color="blue" title={t("company.rename_pending_title")} data-testid="rename-pending">
          {t("company.rename_pending_body", { name: c.text_name })}
        </Alert>
      )}
      {editing ? (
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Title order={2} size="h4">
              {t("company.edit")}
            </Title>
            <Text size="sm" c="dimmed">
              {t("company.rename_note")}
            </Text>
            <CompanyForm
              initial={c}
              submitLabel={t("company.save")}
              onSubmit={save}
              onCancel={() => setEditing(false)}
            />
          </Stack>
        </Paper>
      ) : (
        <>
          <Table aria-label={t("company.details")} data-testid="company-details">
            <Table.Tbody>
              {rows.map(([label, value]) => (
                <Table.Tr key={label}>
                  <Table.Th scope="row" w="40%">
                    {label}
                  </Table.Th>
                  <Table.Td style={{ wordBreak: "break-word" }}>{value}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {admin ? (
            <Button
              variant="light"
              w="fit-content"
              onClick={() => {
                setSaved(false);
                setEditing(true);
              }}
            >
              {t("company.edit")}
            </Button>
          ) : (
            <Text size="sm" c="dimmed">
              {t("company.viewer_note")}
            </Text>
          )}
        </>
      )}
    </Stack>
  );
}
